import React, { useCallback } from "react";
import { map, round, take } from "lodash";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  AvatarProps as BaseAvatarProps,
} from "@/shared/ui/AvatarBase";
import { Center, Column, Row } from "@/shared/ui/Blocks";
import { Icon } from "@/shared/ui/Icon";
import avatarColor from "./avatarColor";
import { cn } from "@/shared/utils/shadcn";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";

interface AvatarProps extends BaseAvatarProps {
  name?: string;
  onClick?: () => void;
  size?: keyof typeof avatarSizes;
  avatar?: string;
  initials?: string;
  FallbackElement?: React.ReactNode;
  isBlank?: boolean;
  isContact?: boolean;
}

interface AvatarWithNameProps extends AvatarProps {
  description?: string;
  direction?: "row" | "column" | "row-reverse";
  wrapperClassName?: string;
}

interface AvatarWithCounterProps extends AvatarProps {
  count?: number;
  label?: string;
  AvatarComponent?: React.ReactNode;
}

export interface AvatarWithScoreProps extends AvatarWithNameProps {
  score?: number;
  scoreClassName?: string;
}

const avatarSizes = {
  xs: "w-6 h-6",
  sm: "w-8 h-8",
  md: "w-10 h-10",
  lg: "w-12 h-12",
  xl: "w-16 h-16",
  "2xl": "w-[104px] h-[104px]",
};

const avatarTextSizes = {
  xs: "text-2xs",
  sm: "text-xs",
  md: "text-sm",
  lg: "text-sm",
  xl: "text-md",
  "2xl": "text-lg",
};

function AvatarWithInitials(props: AvatarProps) {
  const {
    avatar,
    initials,
    name,
    onClick,
    size = "md",
    className = "",
    FallbackElement,
    isBlank,
    isContact,
    ...rest
  } = props;

  const avatarSizeClass = avatarSizes[size];

  const isBlankAvatar = isBlank || (!avatar && !initials);

  const avatarColorClasses = avatarColor(initials, isBlankAvatar || isContact);

  function FallbackComponent() {
    if (FallbackElement) {
      return FallbackElement;
    }

    if (isContact) {
      return <Icon icon="Incognito" color="darkGrey" size="sm" />;
    }

    if (isBlankAvatar) {
      return <Icon icon="EmailEmpty" color="darkGrey" size="sm" />;
    }

    const textSize = avatarTextSizes[size];

    return (
      <span
        className={cn(
          "text-center font-medium",
          textSize,
          avatarColorClasses.text
        )}
      >
        {initials?.toUpperCase()}
      </span>
    );
  }

  const AvatarComponent = useCallback(() => {
    return (
      <Avatar
        {...rest}
        onClick={onClick}
        className={cn(avatarSizeClass, className)}
      >
        <AvatarImage src={avatar} />
        <AvatarFallback
          className={`flex items-center justify-center rounded-full ${avatarColorClasses.bg}`}
        >
          <FallbackComponent />
        </AvatarFallback>
      </Avatar>
    );
  }, [name, avatar]);

  if (name) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <AvatarComponent />
        </TooltipTrigger>
        <TooltipContent side="top">{name}</TooltipContent>
      </Tooltip>
    );
  }

  return <AvatarComponent />;
}

function AvatarWithName(props: AvatarWithNameProps) {
  const {
    name,
    onClick,
    size = "md",
    direction = "column",
    description,
    initials,
    wrapperClassName = "",
    ...rest
  } = props;

  const Wrapper = direction === "column" ? Column : Row;
  const alignmentClasses =
    direction === "column"
      ? "justify-center space-y-2"
      : `gap-2 ${direction === "row-reverse" && "flex-row-reverse"}`;

  return (
    <Wrapper
      className={cn(
        "w-full h-full items-center",
        alignmentClasses,
        wrapperClassName
      )}
    >
      <AvatarWithInitials
        onClick={onClick}
        size={size}
        initials={initials || name?.charAt(0)}
        {...rest}
      />
      <Column className="max-w-full">
        {!!name && (
          <span className="text-sm font-semibold text-gray-700 truncate max-w-full block">
            {name}
          </span>
        )}
        {!!description && (
          <span className="text-xs text-gray-500 truncate max-w-full block">
            {description}
          </span>
        )}
      </Column>
    </Wrapper>
  );
}

function AvatarWithCounterBadge(props: AvatarWithCounterProps) {
  const {
    count,
    label,
    size = "md",
    AvatarComponent,
    className,
    ...rest
  } = props;
  const showBadge = !!count && count > 0;
  return (
    <Column
      className={cn(
        "w-full h-full items-center justify-center space-y-2",
        className
      )}
    >
      <Row className="relative items-center">
        {AvatarComponent || (
          <AvatarWithInitials size={size} {...rest} className="mr-3" />
        )}
        {showBadge && (
          <Center className="absolute z-10 right-0 bg-orange-100 rounded-full aspect-square h-8">
            <span className="text-xs font-semibold text-orange-500">{`+${count}`}</span>
          </Center>
        )}
      </Row>
      {!!label && <span className="text-xs  text-gray-700">{label}</span>}
    </Column>
  );
}

function AvatarWithScore(props: AvatarWithScoreProps) {
  const { score = 0, scoreClassName, size = "md", ...rest } = props;
  return (
    <Column className="w-full h-full items-center justify-center space-y-2">
      <Column className="relative items-center">
        <AvatarWithInitials size={size} {...rest} />
        <Center
          className={cn(
            "absolute z-10 self-center -bottom-3 bg-gray-900 rounded-full aspect-square h-6",
            scoreClassName
          )}
        >
          <span className="text-xs font-semibold text-white">
            {round(score)}
          </span>
        </Center>
      </Column>
    </Column>
  );
}

interface AvatarRowProps {
  visibleCount?: number;
  avatars: AvatarProps[];
  className?: string;
}

export function AvatarRow(props: AvatarRowProps) {
  const { visibleCount = 5, avatars, ...rest } = props;
  const visibleAvatars = take(avatars, visibleCount);
  return (
    <Row className="-space-x-2" {...rest}>
      {map(visibleAvatars, (avatar, index) => (
        <AvatarWithInitials
          key={index}
          initials={avatar.initials || ""}
          avatar={avatar.avatar || ""}
          size="sm"
        />
      ))}
      {visibleCount < avatars.length && (
        <AvatarWithCounterBadge
          avatar={avatars[visibleCount].avatar || ""}
          initials={avatars[visibleCount].initials || ""}
          count={avatars.length - visibleCount - 1}
          size="sm"
          className="w-8 h-8"
        />
      )}
    </Row>
  );
}

export {
  AvatarWithInitials,
  AvatarWithName,
  AvatarWithCounterBadge,
  AvatarWithScore,
};
